import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Home } from "@/pages/Home";
import { AppHome } from "@/pages/AppHome";
import { CommandGate } from "@/pages/CommandGate";
import { SsoCallback } from "@/pages/SsoCallback";
import { OrgGate } from "@/pages/OrgGate";
import { MarketingOnly } from "@/pages/MarketingOnly";
import { PrivacyPage, TermsPage } from "@/pages/LegalPage";
import { hostMode } from "@/lib/hosts";
import { CommandBootScreen } from "@/components/CommandBootScreen";
import { DocsLayout } from "@/components/docs/DocsLayout";
import {
  DocsHome,
  DocsOss,
  DocsRailway,
  DocsConnect,
  DocsRadio,
  DocsIm,
  DocsMcp,
  DocsSkill,
  DocsAuth,
  DocsHubs,
} from "@/pages/docs/DocsPages";

/** Marketing host — self-host product; no SaaS Command redirect. */
function MarketingHome() {
  return <Home />;
}

const hasClerk = Boolean(
  (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined)?.trim()
);

/** Signed-in → org required → shell; signed-out → branded Command gate. */
function ProtectedApp() {
  const { isLoaded, isSignedIn, orgId } = useAuth();
  const [slow, setSlow] = useState(false);

  // After email 2FA, Clerk can take a moment; surface recovery if stuck
  useEffect(() => {
    if (isLoaded) {
      setSlow(false);
      return;
    }
    const t = window.setTimeout(() => setSlow(true), 12_000);
    return () => window.clearTimeout(t);
  }, [isLoaded]);

  if (!isLoaded) {
    return (
      <div className="relative">
        <CommandBootScreen
          detail={
            slow
              ? "Still loading auth — if you just verified email, wait a moment."
              : "Loading your workspace…"
          }
        />
        {slow ? (
          <div className="absolute bottom-10 left-0 right-0 flex justify-center">
            <button
              type="button"
              className="text-xs text-orange-400 hover:text-orange-300"
              onClick={() => window.location.assign("/")}
            >
              Refresh Command
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  // Path routes only — OAuth/SSO must land on /sign-in, not inline gate at /
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // Gate only on missing active org. Do NOT re-open OrgGate when orgId is set
  // but a choose-organization session task is still clearing — that flash-looped
  // CreateOrganization / TaskChooseOrganization after every successful create.
  if (!orgId) {
    return <OrgGate />;
  }

  return <AppHome />;
}

export default function App() {
  const mode = hostMode();

  // docs.opengateways.xyz — public self-host docs (no Clerk required)
  if (mode === "docs") {
    return (
      <BrowserRouter>
        <Routes>
          <Route element={<DocsLayout />}>
            <Route path="/" element={<DocsHome />} />
            <Route path="/saas" element={<Navigate to="/oss" replace />} />
            <Route path="/oss" element={<DocsOss />} />
            <Route path="/railway" element={<DocsRailway />} />
            <Route path="/compare" element={<Navigate to="/oss" replace />} />
            <Route path="/agents/connect" element={<DocsConnect />} />
            <Route path="/agents/radio" element={<DocsRadio />} />
            <Route path="/agents/im" element={<DocsIm />} />
            <Route path="/agents/mcp" element={<DocsMcp />} />
            <Route path="/agents/skill" element={<DocsSkill />} />
            <Route path="/auth" element={<DocsAuth />} />
            <Route path="/hubs" element={<DocsHubs />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    );
  }

  // command host retired — send people to marketing / GitHub
  if (mode === "command") {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<MarketingOnly />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (!hasClerk) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MarketingOnly />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // localhost: marketing + /app dashboard for local dev (+ ?host=docs)
  if (mode === "local") {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/app" element={<ProtectedApp />} />
          <Route path="/app/*" element={<ProtectedApp />} />
          <Route path="/create-org" element={<ProtectedApp />} />
          <Route path="/sign-in/*" element={<CommandGate />} />
          <Route path="/sign-up/*" element={<CommandGate />} />
          <Route path="/sso-callback" element={<SsoCallback />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // opengateways.xyz / www — marketing (self-host)
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MarketingHome />} />
        <Route path="/app" element={<Navigate to="/" replace />} />
        <Route path="/app/*" element={<Navigate to="/" replace />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
